import { useContext, useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { DataTable } from "@/components/data-table";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressao } from "@/components/ImpressaoComponent";
import { MODOS_IMPRESSAO } from "@/lib/modosImpressao";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import {
  InfoComplField,
  getPreDefinicoesUnicas,
  infoComplObrigatoriasFaltando,
} from "@/components/InfoComplField";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { AdminContext } from "@/contexts/AdminContext";
import { useCategorias } from "@/contexts/CategoriasContext";
import { useContaConfig } from "@/contexts/ContaConfigContext";
import { useEpc } from "@/contexts/EpcContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useItem } from "@/contexts/ItemContext";
import { useLocalizacoes } from "@/contexts/LocalizacoesContext";
import { useSeriais } from "@/contexts/SeriaisContext";
import { alert } from "@/contexts/AlertModalContext";

import inovaone_api from "@/services/inovaone_api";
import inovaone_api_integracoes from "@/services/inovaone_api_integracoes";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { EpcItemGerado } from "@/types/epc-item-gerado";
import type { ImpressaoItem } from "@/types/impressao-item";
import type { NewCategoriaItem } from "@/types/new-categoria-item";
import type { OFRefCategoriaItemResponse } from "@/types/of-ref-categoria-item-response";
import type { PedidoItemResponse } from "@/types/pedido-item-response";
import {
  brDateToISO,
  converterListaZplItemParaUrl,
  extrairToken,
  formatarData,
  sanitizarCodigoHex,
  verificarEpcsInvalidos,
} from "@/utils/UtilFuncoes";

import type { ColumnDef } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import {
  AlertTriangle,
  ExternalLink,
  Eye,
  ImageOff,
  Inbox,
  Layers,
  MapPin,
  PackagePlus,
  Pencil,
  Printer,
  RefreshCw,
  Search,
  X,
} from "lucide-react";

// =====================================================
// Células extraídas (evitam useState dentro do `cell:`)
// =====================================================

// Aplica uma nova quantidade a um item, recalculando quantidade_bo (e a info
// complementar mapeada) quando a conta trava a quantidade pelo ERP — a mesma
// regra usada na edição individual e na edição em massa.
function aplicarQuantidadeNoItem(
  item: OFRefCategoriaItemResponse,
  novaQuantidade: number,
  travarQtdErp: boolean | undefined,
  target: number | undefined,
  campoQuantidadePedidos: string | undefined,
): OFRefCategoriaItemResponse {
  if (
    travarQtdErp &&
    target !== undefined &&
    target > 0 &&
    novaQuantidade > 0
  ) {
    // A quantidade_bo (qtd por etiqueta) pode ser decimal — basta que
    // quantidade × quantidade_bo = target. Ex.: 34.05 / 3 = 11.35.
    const newQuantidadeBo = target / novaQuantidade;
    const atualizado: OFRefCategoriaItemResponse = {
      ...item,
      quantidade: novaQuantidade,
      quantidade_bo: newQuantidadeBo,
    };
    if (campoQuantidadePedidos) {
      const infoMatch = campoQuantidadePedidos.match(/inf_compl_(\d+)/);
      if (infoMatch) {
        (atualizado as Record<string, unknown>)[`info${infoMatch[1]}`] =
          String(newQuantidadeBo);
      }
    }
    return atualizado;
  }
  return { ...item, quantidade: novaQuantidade };
}

function EditarEmMassaButton({
  travarQtdErp,
  targetsProduto,
  campoQuantidadePedidos,
  infoLabels,
  setCategoriasExternas,
}: {
  travarQtdErp: boolean | undefined;
  targetsProduto: number[];
  campoQuantidadePedidos: string | undefined;
  infoLabels: string[];
  setCategoriasExternas: React.Dispatch<
    React.SetStateAction<OFRefCategoriaItemResponse[]>
  >;
}) {
  const [open, setOpen] = useState(false);
  // "quantidade" ou "info1".."info9"
  const [campo, setCampo] = useState("quantidade");
  const [quantidade, setQuantidade] = useState(1);
  const [valorInfo, setValorInfo] = useState("");

  useEffect(() => {
    if (!open) {
      setCampo("quantidade");
      setQuantidade(1);
      setValorInfo("");
    }
  }, [open]);

  const handleAplicar = () => {
    if (campo === "quantidade") {
      setCategoriasExternas((prev) =>
        prev.map((item, idx) =>
          aplicarQuantidadeNoItem(
            item,
            quantidade,
            travarQtdErp,
            targetsProduto[idx],
            campoQuantidadePedidos,
          ),
        ),
      );
    } else {
      setCategoriasExternas((prev) =>
        prev.map((item) => ({ ...item, [campo]: valorInfo })),
      );
    }
    setOpen(false);
  };

  const infoIdx = Number(campo.match(/(\d+)$/)?.[1] ?? 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-xs">
          <Pencil className="h-3.5 w-3.5" />
          Editar em massa
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="grid gap-2">
          <Label className="text-xs">Campo</Label>
          <Select
            value={campo}
            onValueChange={(v) => {
              setCampo(v);
              setValorInfo("");
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="quantidade">Quantidade</SelectItem>
              {infoLabels.map((label, i) => (
                <SelectItem key={i} value={`info${i + 1}`}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label className="text-xs">Valor para todos os itens</Label>
          {campo === "quantidade" ? (
            <Input
              type="number"
              value={quantidade}
              onChange={(e) => {
                const value = e.target.value;
                setQuantidade(value === "" ? 0 : Number(value));
              }}
              min={0}
            />
          ) : (
            <InfoComplField
              idx={infoIdx}
              value={valorInfo}
              onChange={setValorInfo}
            />
          )}
          {campo === "quantidade" && travarQtdErp && (
            <span className="text-[10px] text-muted-foreground">
              A qtd por etiqueta (quantidade_bo) será recalculada em cada item
              para manter o total do ERP.
            </span>
          )}
          <Button size="sm" onClick={handleAplicar}>
            Aplicar a todos
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function QuantidadeCell({
  row,
  travarQtdErp,
  targetsProduto,
  campoQuantidadePedidos,
  setCategoriasExternas,
}: {
  row: { index: number; original: OFRefCategoriaItemResponse };
  travarQtdErp: boolean | undefined;
  targetsProduto: number[];
  campoQuantidadePedidos: string | undefined;
  setCategoriasExternas: React.Dispatch<
    React.SetStateAction<OFRefCategoriaItemResponse[]>
  >;
}) {
  const { quantidade } = row.original;
  const [open, setOpen] = useState(false);
  const [quantidadeAlterada, setQuantidadeAlterada] = useState(quantidade);

  useEffect(() => {
    if (!open) setQuantidadeAlterada(quantidade);
  }, [open, quantidade]);

  const handleSalvar = () => {
    setCategoriasExternas((prev) =>
      prev.map((item, idx) =>
        idx === row.index
          ? aplicarQuantidadeNoItem(
              item,
              quantidadeAlterada,
              travarQtdErp,
              targetsProduto[row.index],
              campoQuantidadePedidos,
            )
          : item,
      ),
    );
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm">
          {quantidade > 0 ? (
            <Badge variant="secondary" className="font-normal">
              {quantidade}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">0</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60">
        <div className="grid gap-2">
          <Label className="text-xs">Quantidade</Label>
          <Input
            type="number"
            value={quantidadeAlterada}
            onChange={(e) => {
              const value = e.target.value;
              setQuantidadeAlterada(value === "" ? 0 : Number(value));
            }}
            min={0}
          />
          <Button size="sm" onClick={handleSalvar}>
            Salvar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function InfoComplCell({
  row,
  field,
  label,
  setCategoriasExternas,
}: {
  row: { index: number; original: OFRefCategoriaItemResponse };
  field: string;
  label: string;
  setCategoriasExternas: React.Dispatch<
    React.SetStateAction<OFRefCategoriaItemResponse[]>
  >;
}) {
  const value = ((row.original as any)[field] as string) || "";
  const idx = Number(field.match(/(\d+)$/)?.[1] ?? 0);
  const [open, setOpen] = useState(false);
  const [edited, setEdited] = useState(value);

  useEffect(() => {
    if (!open) setEdited(value);
  }, [open, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="max-w-[200px] truncate">
          {value ? (
            <Badge variant="secondary" className="font-normal">
              {value}
            </Badge>
          ) : (
            <span className="text-xs text-muted-foreground">Editar</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="grid gap-2">
          <Label className="text-xs">{label}</Label>
          <InfoComplField idx={idx} value={edited} onChange={setEdited} />
          <Button
            size="sm"
            onClick={() => {
              setCategoriasExternas((prev) =>
                prev.map((item, idx) =>
                  idx === row.index ? { ...item, [field]: edited } : item,
                ),
              );
              setOpen(false);
            }}
          >
            Salvar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EpcInfoIndividualCell({
  row,
  travarQtdErp,
  setEpcs,
}: {
  row: { index: number; original: EpcItemGerado };
  travarQtdErp: boolean | undefined;
  setEpcs: React.Dispatch<React.SetStateAction<EpcItemGerado[]>>;
}) {
  const { quantidade_bo, valor_bo, dt_validade_bo } = row.original;
  const [open, setOpen] = useState(false);
  const [quantidade, setQuantidade] = useState(quantidade_bo || "");
  const [valor, setValor] = useState(valor_bo ?? 0);
  const [validade, setValidade] = useState(dt_validade_bo || "");

  useEffect(() => {
    if (!open) {
      setQuantidade(quantidade_bo || "");
      setValor(valor_bo ?? 0);
      setValidade(dt_validade_bo || "");
    }
  }, [open, quantidade_bo, valor_bo, dt_validade_bo]);

  const temValores = !!quantidade || valor > 0 || !!validade;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant={temValores ? "outline" : "ghost"} size="sm">
          {temValores ? (
            <div className="flex flex-wrap gap-1">
              {quantidade && (
                <Badge variant="secondary" className="font-normal">
                  Qtd: {quantidade}
                </Badge>
              )}
              {valor > 0 && (
                <Badge variant="secondary" className="font-normal">
                  R$ {valor.toFixed(2)}
                </Badge>
              )}
              {validade && (
                <Badge variant="secondary" className="font-normal">
                  Val: {validade}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">+ Atribuir</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Quantidade</Label>
            <Input
              type="number"
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
              min={0}
              disabled={!!travarQtdErp}
            />
            {travarQtdErp && (
              <span className="text-[10px] text-muted-foreground">
                Bloqueado — altere a quantidade na tabela de categorias.
              </span>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Valor (R$)</Label>
            <Input
              type="number"
              step="0.01"
              value={valor}
              onChange={(e) => {
                const v = e.target.value;
                setValor(v === "" ? 0 : Number(v));
              }}
              min={0}
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Validade</Label>
            <Input
              type="date"
              value={validade}
              onChange={(e) => setValidade(formatarData(e.target.value, false))}
            />
          </div>
          <Button
            size="sm"
            onClick={() => {
              setEpcs((prev) =>
                prev.map((item, idx) =>
                  idx === row.index
                    ? {
                        ...item,
                        quantidade_bo: quantidade,
                        valor_bo: valor === 0 ? undefined : Number(valor),
                        dt_validade_bo: validade,
                      }
                    : item,
                ),
              );
              setOpen(false);
            }}
          >
            Salvar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PreviewCell({
  item,
  coletaZplPadrao,
}: {
  item: EpcItemGerado;
  coletaZplPadrao: () => any;
}) {
  const itemZpl = coletaZplPadrao();
  if (itemZpl && itemZpl.zpl) itemZpl.zpl = item.zpl_impressao_bo;
  if (!itemZpl) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <ImageOff className="h-3.5 w-3.5" />
        ZPL não configurado
      </span>
    );
  }
  const url = converterListaZplItemParaUrl(itemZpl);
  return (
    <HoverCard openDelay={300} closeDelay={50}>
      <HoverCardTrigger asChild>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
        >
          <Eye className="h-3.5 w-3.5" />
          Pré-visualizar
        </a>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-2">
        <img
          src={url}
          alt="Pré-visualização"
          className="w-full h-auto bg-white rounded"
        />
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Abrir em nova aba
        </a>
      </HoverCardContent>
    </HoverCard>
  );
}

// =====================================================
// Componente principal
// =====================================================

// Duração da animação de saída do bloco de resultados (ms).
const RESULTADOS_EXIT_MS = 200;

function ImportarOFREF() {
  const { contaConfig, numeroDeImpressoes } = useContaConfig();
  const { isLoading, setIsLoading } = useGlobalLoader();
  const {
    coletaCategorias,
    pesquisarCategoria,
    criarCategorias,
    categorias,
    atualizarDescricaoCategoria,
  } = useCategorias();
  const { coletaSeriais, seriais, adicionarMaisUmNosSeriais } = useSeriais();
  const { coletaLocalizacoes, localizacoes } = useLocalizacoes();
  const { impressaoConfig, coletarBlocosEPC, coletaZplPadrao } =
    useImpressaoConfig();
  const { gerarEpcs } = useEpc();
  const { pesquisarItensNaBase } = useItem();
  const { requireAdmin } = useContext(AdminContext)!;
  const { adicionarItensNaFila } = useImpressao();

  const [categoriasExternas, setCategoriasExternas] = useState<
    OFRefCategoriaItemResponse[]
  >([]);
  const [localizacaoSelecionada, setLocalizacaoSelecionada] = useState("");
  const [localizacaoSelecionada2, setLocalizacaoSelecionada2] = useState("");
  const [localizacaoSelecionada3, setLocalizacaoSelecionada3] = useState("");
  const [localizacaoSelecionada4, setLocalizacaoSelecionada4] = useState("");
  const [codigoOfRef, setCodigoOfRef] = useState("");
  const [epcs, setEpcs] = useState<EpcItemGerado[]>([]);
  const [permitirItensRepetidos, setPermitirItensRepetidos] = useState(false);
  const [pedidos, setPedidos] = useState<PedidoItemResponse[]>([]);
  const [gerouListaImpressao, setGerouListaImpressao] = useState(false);
  const [epcRepetidosDialog, setEpcRepetidosDialog] = useState(false);
  const [epcRepetidosEncontrados, setEpcRepetidosEncontrados] = useState<
    { epc: string; codigo: string }[]
  >([]);
  const [codigosConvertidos, setCodigosConvertidos] = useState<
    Array<{ original: string; convertido: string }>
  >([]);
  const [duplicatasDialog, setDuplicatasDialog] = useState(false);
  const [pendingCategorias, setPendingCategorias] = useState<
    OFRefCategoriaItemResponse[]
  >([]);
  const [pendingConversoes, setPendingConversoes] = useState<
    Array<{ original: string; convertido: string }>
  >([]);
  const [selectedDuplicataIndices, setSelectedDuplicataIndices] = useState<
    Set<number>
  >(new Set());
  const [targetsProduto, setTargetsProduto] = useState<number[]>([]);
  const [resultadosSaindo, setResultadosSaindo] = useState(false);

  // ---- Preferências persistidas (chave consistente) ----
  useEffect(() => {
    const repetidos = localStorage.getItem("permitirItensRepetidos");
    setPermitirItensRepetidos(repetidos === "true");
  }, []);

  // ---- Helpers ----
  const getQuantidadeBoFromCategoria = (
    categoria: OFRefCategoriaItemResponse,
  ): number => {
    const campo = impressaoConfig?.campo_quantidade_pedidos;
    if (campo) {
      const match = campo.match(/inf_compl_(\d+)/);
      if (match) {
        const key = `info${match[1]}` as keyof OFRefCategoriaItemResponse;
        const val = categoria[key];
        if (val) return Number(val);
      }
    }
    return categoria.quantidade_bo ?? 1;
  };

  // Preenche as infos complementares vazias do pedido com o que está
  // cadastrado na categoria (casada pelo EAN). O valor do pedido (ERP/XLSX)
  // tem prioridade; a categoria só serve de fallback quando o campo vem vazio.
  // Sobrando ainda vazio, cai na pré-definição do campo quando ela tem uma
  // única opção cadastrada (não há o que escolher).
  const enriquecerInfosComCategoria = (
    itens: OFRefCategoriaItemResponse[],
  ): OFRefCategoriaItemResponse[] => {
    const preDefinicoesUnicas = getPreDefinicoesUnicas(impressaoConfig);
    return itens.map((item) => {
      const cat = pesquisarCategoria(item.ean);
      const enriquecido = { ...item };
      for (let i = 1; i <= 9; i++) {
        const infoKey = `info${i}` as keyof OFRefCategoriaItemResponse;
        const complKey = `inf_compl_${i}` as keyof CategoriaItemResponse;
        const atual = String(enriquecido[infoKey] ?? "").trim();
        if (atual) continue;
        const daCategoria = cat ? String(cat[complKey] ?? "").trim() : "";
        const fallback = daCategoria || preDefinicoesUnicas[String(i)] || "";
        if (fallback) {
          (enriquecido as Record<string, unknown>)[infoKey] = fallback;
        }
      }
      return enriquecido;
    });
  };

  // ---- Localização por Info. Complementar (configurada na conta) ----
  const getInfoValue = (
    categoria: OFRefCategoriaItemResponse,
    campo?: string,
  ): string => {
    if (!campo) return "";
    const match = campo.match(/inf_compl_(\d+)/);
    if (!match) return "";
    const key = `info${match[1]}` as keyof OFRefCategoriaItemResponse;
    return String(categoria[key] ?? "").trim();
  };

  // Resolve a localização de um item a partir das infos complementares
  // mapeadas em nivel_loc_X_pedidos, casando o valor com a descrição da
  // localização cadastrada em cada nível (hierárquico).
  const resolverLocPorInfo = (categoria: OFRefCategoriaItemResponse) => {
    const v1 = getInfoValue(categoria, impressaoConfig?.nivel_loc_1_pedidos);
    const v2 = getInfoValue(categoria, impressaoConfig?.nivel_loc_2_pedidos);
    const v3 = getInfoValue(categoria, impressaoConfig?.nivel_loc_3_pedidos);
    const v4 = getInfoValue(categoria, impressaoConfig?.nivel_loc_4_pedidos);
    const n1 = v1
      ? localizacoes.find((loc) => loc.descricao === v1)
      : undefined;
    const n2 =
      n1 && v2 ? n1.children.find((loc) => loc.descricao === v2) : undefined;
    const n3 =
      n2 && v3 ? n2.children.find((loc) => loc.descricao === v3) : undefined;
    const n4 =
      n3 && v4 ? n3.children.find((loc) => loc.descricao === v4) : undefined;
    return {
      id1: n1?._id || "",
      id2: n2?._id || "",
      id3: n3?._id || "",
      id4: n4?._id || "",
      desc1: n1?.descricao || "",
      desc2: n2?.descricao || "",
      desc3: n3?.descricao || "",
      desc4: n4?.descricao || "",
      // considera resolvido quando o nível 1 (principal) foi encontrado
      resolvido: !!n1,
    };
  };

  // Ativa quando a conta configurou ao menos o nível 1 da localização.
  const locPorInfoAtiva = !!impressaoConfig?.nivel_loc_1_pedidos;

  // Considera apenas itens que serão impressos (quantidade > 0).
  const resolucoesLoc = useMemo(
    () =>
      locPorInfoAtiva
        ? categoriasExternas
            .filter((c) => c.quantidade > 0)
            .map((c) => resolverLocPorInfo(c))
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locPorInfoAtiva, categoriasExternas, localizacoes, impressaoConfig],
  );

  const itensSemLocalizacao = locPorInfoAtiva
    ? resolucoesLoc.filter((r) => !r.resolvido).length
    : 0;

  // Todos os itens tiveram localização definida pela Info. Complementar.
  const todosResolvidosPorInfo =
    locPorInfoAtiva &&
    categoriasExternas.length > 0 &&
    itensSemLocalizacao === 0;

  const coletarPedidos = async () => {
    // sem a conta carregada a URL sairia com "undefined" no lugar do id_conta
    if (!contaConfig?.id_conta) return;

    try {
      const response_pedidos = await inovaone_api.get<PedidoItemResponse[]>(
        `/registroPage/${contaConfig?.id_conta}/*/coleta-p/*/*/*/*/*/*/0`,
      );
      if (response_pedidos.data.length > 0) {
        const pedidosCopy = [...response_pedidos.data];
        pedidosCopy.pop();
        setPedidos(pedidosCopy);
      }
    } catch (error) {
      console.error("Erro ao coletar pedidos:", error);
      alert.error("Erro ao coletar pedidos. Tente novamente mais tarde.");
    }
  };

  // ---- Pesquisa ERP ----
  const pesquisarErpInovaone = async (codigo: string) => {
    if (!impressaoConfig) return;
    try {
      await coletaSeriais();
      const response_ofref = await inovaone_api_integracoes.get<
        OFRefCategoriaItemResponse[]
      >(
        `/inovaone/rota?_id=${codigo}&id_conta=${impressaoConfig.id_conta}&id_usuario=${impressaoConfig.id_usuario}`,
      );
      if (response_ofref.data.length === 0) {
        alert.error("Nenhum dado encontrado para o código informado.");
        return;
      }
      let ofRefDataNoRepeat = [...response_ofref.data];
      const conversoes: Array<{ original: string; convertido: string }> = [];
      ofRefDataNoRepeat = ofRefDataNoRepeat.map((item) => {
        const eanHash = sanitizarCodigoHex(item.ean);
        if (
          eanHash !== item.ean.toUpperCase() &&
          !conversoes.some((c) => c.original === item.ean)
        ) {
          conversoes.push({ original: item.ean, convertido: eanHash });
        }
        return { ...item, ean: eanHash };
      });
      ofRefDataNoRepeat = enriquecerInfosComCategoria(ofRefDataNoRepeat);
      const eanCountsInovaone: Record<string, number> = {};
      ofRefDataNoRepeat.forEach((item) => {
        eanCountsInovaone[item.ean] = (eanCountsInovaone[item.ean] || 0) + 1;
      });
      if (Object.values(eanCountsInovaone).some((c) => c > 1)) {
        setSelectedDuplicataIndices(
          new Set(ofRefDataNoRepeat.map((_, idx) => idx)),
        );
        setPendingCategorias(ofRefDataNoRepeat);
        setPendingConversoes(conversoes);
        setDuplicatasDialog(true);
      } else {
        setCodigosConvertidos(conversoes);
        setCategoriasExternas(ofRefDataNoRepeat);
        setTargetsProduto(
          ofRefDataNoRepeat.map(
            (c) => (c.quantidade || 1) * getQuantidadeBoFromCategoria(c),
          ),
        );
      }
    } catch (error: AxiosError | any) {
      console.error("Erro ao buscar OF:", error);
    }
  };

  const pesquisarErpExterno = async (codigo: string) => {
    if (!impressaoConfig?.api_externa) {
      pesquisarErpInovaone(codigo);
      return;
    }
    const token = extrairToken(impressaoConfig.api_externa);
    if (!token) {
      alert.error("Token de ERP não encontrado ou inválido.");
      return;
    }
    setIsLoading(true);
    try {
      await coletaSeriais();
      const api = inovaone_api_integracoes;
      const response_ofref = await api.get<OFRefCategoriaItemResponse[]>(
        `/${token}/rota?_id=${codigo}&id_conta=${impressaoConfig.id_conta}&id_usuario=${impressaoConfig.id_usuario}`,
      );
      if (response_ofref.data.length === 0) {
        alert.error("Nenhum dado encontrado para o código informado.");
        return;
      }
      let ofRefDataNoRepeat = [...response_ofref.data];
      const conversoes2: Array<{ original: string; convertido: string }> = [];
      ofRefDataNoRepeat = ofRefDataNoRepeat.map((item) => {
        const eanHash = sanitizarCodigoHex(item.ean);
        if (
          eanHash !== item.ean.toUpperCase() &&
          !conversoes2.some((c) => c.original === item.ean)
        ) {
          conversoes2.push({ original: item.ean, convertido: eanHash });
        }
        return { ...item, ean: eanHash };
      });
      ofRefDataNoRepeat = enriquecerInfosComCategoria(ofRefDataNoRepeat);
      const eanCountsExterno: Record<string, number> = {};
      ofRefDataNoRepeat.forEach((item) => {
        eanCountsExterno[item.ean] = (eanCountsExterno[item.ean] || 0) + 1;
      });
      if (Object.values(eanCountsExterno).some((c) => c > 1)) {
        setSelectedDuplicataIndices(
          new Set(ofRefDataNoRepeat.map((_, idx) => idx)),
        );
        setPendingCategorias(ofRefDataNoRepeat);
        setPendingConversoes(conversoes2);
        setDuplicatasDialog(true);
      } else {
        setCodigosConvertidos(conversoes2);
        setCategoriasExternas(ofRefDataNoRepeat);
        setTargetsProduto(
          ofRefDataNoRepeat.map(
            (c) => (c.quantidade || 1) * getQuantidadeBoFromCategoria(c),
          ),
        );
      }
    } catch (error: AxiosError | any) {
      if (error.response?.status === 404) {
        alert.error(
          `Erro [${token.toUpperCase()}] não integrado, acione o suporte.`,
        );
        return;
      } else {
        console.error("Erro ao buscar OF:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ---- Gerar lista pra impressão ----
  const gerarListaParaImpressao = async () => {
    if (!categoriasExternas.length) {
      alert.error("Selecione uma categoria para impressão.");
      return;
    }
    if (!impressaoConfig) {
      alert.error("Configuração de impressão não encontrada.");
      return;
    }
    if (!impressaoConfig?.app_id) {
      alert.error(
        "Informe o Hardware ID nas configurações para realizar a impressão.",
      );
      return;
    }
    // Quando a localização por Info. Complementar cobre todos os itens, a
    // seleção manual não é necessária. Caso contrário, ela é exigida para os
    // itens que não tiveram localização definida pela info.
    if (!todosResolvidosPorInfo && !localizacaoSelecionada) {
      alert.error("Selecione uma localização para impressão.");
      return;
    }

    const categoriasFormatadas: NewCategoriaItem[] = categoriasExternas
      .filter((categoria) => categoria.quantidade > 0)
      .map((categoria) => ({
        descricao: categoria.descricao,
        ean: categoria.ean,
        id_conta: impressaoConfig.id_conta,
        inf_compl_1: categoria.info1 || "",
        inf_compl_2: categoria.info2 || "",
        inf_compl_3: categoria.info3 || "",
        inf_compl_4: categoria.info4 || "",
        inf_compl_5: categoria.info5 || "",
        inf_compl_6: categoria.info6 || "",
        inf_compl_7: categoria.info7 || "",
        inf_compl_8: categoria.info8 || "",
        inf_compl_9: categoria.info9 || "",
        inf_compl_10: "",
        inf_compl_11: "",
        inf_compl_12: "",
      }));

    // Bloqueia a impressão se alguma info. complementar obrigatória estiver
    // vazia em qualquer um dos itens. Reúne os índices pendentes do lote.
    const faltandoObrigatorios = new Set<number>();
    for (const categoria of categoriasFormatadas) {
      for (const idx of infoComplObrigatoriasFaltando(impressaoConfig, categoria)) {
        faltandoObrigatorios.add(idx);
      }
    }
    if (faltandoObrigatorios.size > 0) {
      const nomes = Array.from(faltandoObrigatorios)
        .sort((a, b) => a - b)
        .map(
          (idx) =>
            (contaConfig as any)?.[`info_compl_${idx}`] || `Info. Compl. ${idx}`,
        );
      alert.error(
        `Preencha os campos obrigatórios antes de imprimir: ${nomes.join(", ")}.`,
      );
      return;
    }

    const categoriasCriadas: CategoriaItemResponse[] = [];
    for (const categoria of categoriasFormatadas) {
      const categoriaExistente = pesquisarCategoria(categoria.ean);
      const categoriaCriada = categoriasCriadas.find(
        (cat) => cat.ean === categoria.ean,
      );
      if (!categoriaExistente && !categoriaCriada) {
        const novaCategoria = await criarCategorias([categoria]);
        if (novaCategoria && novaCategoria.length > 0) {
          if (novaCategoria[0]._id) {
            categoriasCriadas.push(novaCategoria[0]);
          }
        }
      } else {
        if (categoriaExistente) {
          if (
            categoria.descricao.trim() !== "" &&
            categoriaExistente.descricao.trim() !== categoria.descricao.trim()
          ) {
            const atualizada = await atualizarDescricaoCategoria(
              categoria.ean,
              categoria.descricao,
            );
            categoriasCriadas.push(atualizada || categoriaExistente);
          } else {
            categoriasCriadas.push(categoriaExistente);
          }
        } else if (categoriaCriada) {
          categoriasCriadas.push(categoriaCriada);
        } else {
          alert.error(
            `Categoria com EAN ${categoria.ean} não encontrada na base.`,
          );
          return;
        }
      }
    }

    const categoriasECategoriasCriadas: CategoriaItemResponse[] = [
      ...categorias,
      ...categoriasCriadas,
    ];

    const listaParaImpressao: ImpressaoItem[] = [];
    let epcIndex = 0;
    let numero_de_impressoes = numeroDeImpressoes || 0;
    categoriasExternas.flatMap((categoria) => {
      const locInfo = locPorInfoAtiva ? resolverLocPorInfo(categoria) : null;
      const usarLocInfo = !!locInfo?.resolvido;
      const idLoc1 = usarLocInfo ? locInfo!.id1 : localizacaoSelecionada;
      const idLoc2 = usarLocInfo ? locInfo!.id2 : localizacaoSelecionada2;
      const idLoc3 = usarLocInfo ? locInfo!.id3 : localizacaoSelecionada3;
      const idLoc4 = usarLocInfo ? locInfo!.id4 : localizacaoSelecionada4;
      for (let i = 0; i < categoria.quantidade; i++) {
        listaParaImpressao.push({
          _id: epcs[epcIndex]._id,
          inf_compl_1: categoria.info1 || "",
          inf_compl_2: categoria.info2 || "",
          inf_compl_3: categoria.info3 || "",
          inf_compl_4: categoria.info4 || "",
          inf_compl_5: categoria.info5 || "",
          inf_compl_6: categoria.info6 || "",
          inf_compl_7: categoria.info7 || "",
          inf_compl_8: categoria.info8 || "",
          inf_compl_9: categoria.info9 || "",
          inf_compl_10: "",
          inf_compl_11: "",
          inf_compl_12: "",
          codigo: categoria.ean,
          descricao: categoria.descricao,
          tag: epcs[epcIndex].epc,
          zpl_impressao: epcs[epcIndex].zpl_impressao_bo,
          id_categoria:
            categoriasECategoriasCriadas.find(
              (cat) => cat.ean === categoria.ean,
            )?._id || "",
          id_conta: impressaoConfig.id_conta,
          id_usuario: impressaoConfig.id_usuario,
          id_localizacao1: idLoc1,
          id_localizacao2: idLoc2,
          id_localizacao3: idLoc3,
          id_localizacao4: idLoc4,
          valor: epcs[epcIndex].valor_bo || 0,
          quantidade: epcs[epcIndex].quantidade_bo || "",
          dt_validade: brDateToISO(epcs[epcIndex].dt_validade_bo) || "",
          n_impressao: numero_de_impressoes?.toString(),
          id_externo: "",
          ultima_localizacao: {
            verificar: contaConfig?.transferencia_loc_origem || false,
            primeira_vez: true,
          },
        });
        epcIndex++;
        numero_de_impressoes++;
      }
    });
    if (listaParaImpressao.length === 0) {
      alert.error("Nenhum EPC gerado para impressão.");
      return;
    }
    if (!permitirItensRepetidos) {
      const itensNaBase = await pesquisarItensNaBase(
        listaParaImpressao.map((item) => item.tag),
      );
      if (itensNaBase.length > 0) {
        setEpcRepetidosEncontrados(
          listaParaImpressao
            .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
            .map((item) => ({ epc: item.tag, codigo: item.codigo })),
        );
        setEpcRepetidosDialog(true);
        adicionarMaisUmNosSeriais(
          listaParaImpressao
            .filter((item) => itensNaBase.some((i) => i.tag === item.tag))
            .map((item) => item.codigo),
        );
        return;
      }
    }
    const epcs_para_impressao = listaParaImpressao.map((item) => item.tag);
    if (verificarEpcsInvalidos(epcs_para_impressao)) {
      alert.error(
        "EPCs inválidos encontrados. Verifique os dados e tente novamente.",
      );
      return;
    }
    if (impressaoConfig.travar_quantidade_erp && targetsProduto.length > 0) {
      let epcOffset = 0;
      for (let i = 0; i < categoriasExternas.length; i++) {
        const categoria = categoriasExternas[i];
        if (categoria.quantidade <= 0) continue;
        const target = targetsProduto[i];
        if (!target) {
          epcOffset += categoria.quantidade;
          continue;
        }
        const qBo = Number(epcs[epcOffset]?.quantidade_bo || 1);
        const actualProduct = categoria.quantidade * qBo;
        // Comparação com tolerância: quantidade_bo pode ser decimal e o
        // produto sofre imprecisão de ponto flutuante (ex.: 7 × 4.8642...).
        if (Math.abs(actualProduct - target) > 1e-6) {
          alert.error(
            `"${categoria.descricao || categoria.ean}": quantidade × quantidade_bo deve ser ${target} (atual: ${actualProduct}).`,
          );
          return;
        }
        epcOffset += categoria.quantidade;
      }
    }

    adicionarItensNaFila(
      listaParaImpressao,
      undefined,
      undefined,
      MODOS_IMPRESSAO.PEDIDO,
      `OF: ${codigoOfRef}`,
    );
    setCategoriasExternas([]);
    setEpcs([]);
    setTargetsProduto([]);
    setGerouListaImpressao(true);
 
  };

  // ---- Sincronizar ----
  const sincronizar = async () => {
    await coletaSeriais();
    await coletaLocalizacoes();
    await coletaCategorias();
    setCategoriasExternas([]);
    setEpcs([]);
    setTargetsProduto([]);
    alert.success("Dados sincronizados com sucesso!");
  };

  // ---- Limpar resultados ----
  // O unmount espera a animação de saída terminar (RESULTADOS_EXIT_MS casa com
  // a duração da classe animate-out aplicada no bloco de resultados).
  const limparResultados = () => {
    if (resultadosSaindo) return;
    setResultadosSaindo(true);
    window.setTimeout(() => {
      setCategoriasExternas([]);
      setCodigosConvertidos([]);
      setTargetsProduto([]);
      setEpcs([]);
      setCodigoOfRef("");
      setResultadosSaindo(false);
    }, RESULTADOS_EXIT_MS);
  };

  // ---- Effects ----
  useEffect(() => {
    if (impressaoConfig && localizacoes.length > 0) {
      const n1 = localizacoes.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao,
      );
      setLocalizacaoSelecionada(n1?._id || "");
      const n2 = n1?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_2,
      );
      setLocalizacaoSelecionada2(n2?._id || "");
      const n3 = n2?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_3,
      );
      setLocalizacaoSelecionada3(n3?._id || "");
      const n4 = n3?.children?.find(
        (loc) => loc.descricao === impressaoConfig.localizacao_padrao_4,
      );
      setLocalizacaoSelecionada4(n4?._id || "");
    }
    if (gerouListaImpressao) setGerouListaImpressao(false);
    coletarPedidos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impressaoConfig, localizacoes, gerouListaImpressao]);

  useEffect(() => {
    if (categoriasExternas.length > 0) {
      const serialPorEan: Record<string, number> = {};
      const novosEpcs = categoriasExternas.flatMap((categoria) => {
        if (categoria.quantidade <= 0) return [];

        if (serialPorEan[categoria.ean] === undefined) {
          serialPorEan[categoria.ean] =
            (seriais.find((s) => s.gtin === categoria.ean)?.serial ?? -1) + 1;
        }
        const serialAtual = serialPorEan[categoria.ean];

        const locInfo = locPorInfoAtiva ? resolverLocPorInfo(categoria) : null;
        const localizacoesZpl: string[] = locInfo?.resolvido
          ? [locInfo.desc1, locInfo.desc2, locInfo.desc3, locInfo.desc4]
          : [
              localizacoes.find((loc) => loc._id === localizacaoSelecionada)
                ?.descricao || "",
              localizacoes
                .find((n1) => n1._id === localizacaoSelecionada)
                ?.children.find((loc) => loc._id === localizacaoSelecionada2)
                ?.descricao || "",
              localizacoes
                .find((n1) => n1._id === localizacaoSelecionada)
                ?.children.find((n2) => n2._id === localizacaoSelecionada2)
                ?.children.find((loc) => loc._id === localizacaoSelecionada3)
                ?.descricao || "",
              localizacoes
                .find((n1) => n1._id === localizacaoSelecionada)
                ?.children.find((n2) => n2._id === localizacaoSelecionada2)
                ?.children.find((n3) => n3._id === localizacaoSelecionada3)
                ?.children.find((loc) => loc._id === localizacaoSelecionada4)
                ?.descricao || "",
            ];
        const epcsGerados = gerarEpcs(
          {
            _id: pesquisarCategoria(categoria.ean)?._id || "",
            ean: categoria.ean,
            descricao: categoria.descricao,
            id_conta: impressaoConfig?.id_conta || "",
            inf_compl_1: categoria.info1 || "",
            inf_compl_2: categoria.info2 || "",
            inf_compl_3: categoria.info3 || "",
            inf_compl_4: categoria.info4 || "",
            inf_compl_5: categoria.info5 || "",
            inf_compl_6: categoria.info6 || "",
            inf_compl_7: categoria.info7 || "",
            inf_compl_8: categoria.info8 || "",
            inf_compl_9: categoria.info9 || "",
            inf_compl_10: "",
            inf_compl_11: "",
            inf_compl_12: "",
          },
          categoria.quantidade,
          localizacoesZpl,
          coletarBlocosEPC(),
          serialAtual,
        );
        serialPorEan[categoria.ean] = serialAtual + categoria.quantidade;
        if (impressaoConfig?.travar_quantidade_erp) {
          const qBo = getQuantidadeBoFromCategoria(categoria);
          return epcsGerados.map((epc) => ({
            ...epc,
            quantidade_bo: String(qBo),
          }));
        }
        return epcsGerados;
      });
      setEpcs(novosEpcs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriasExternas]);

  // ---- Colunas ----
  const columnsOFREFCategoria: ColumnDef<OFRefCategoriaItemResponse>[] = [
    {
      accessorKey: "quantidade",
      header: "Qtd",
      cell: ({ row }) => (
        <QuantidadeCell
          row={row}
          travarQtdErp={impressaoConfig?.travar_quantidade_erp}
          targetsProduto={targetsProduto}
          campoQuantidadePedidos={impressaoConfig?.campo_quantidade_pedidos}
          setCategoriasExternas={setCategoriasExternas}
        />
      ),
    },
    {
      accessorKey: "ean",
      header: "Código",
      cell: ({ row }) => {
        const ean: string = row.getValue("ean");
        const conversion = codigosConvertidos.find((c) => c.convertido === ean);
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs">{ean}</span>
            {conversion && (
              <Badge
                variant="outline"
                className="text-[10px] text-amber-700 dark:text-amber-400 border-amber-500/30 bg-amber-500/10 gap-1"
              >
                <AlertTriangle className="h-3 w-3" />
                {conversion.original}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "descricao",
      header: "Descrição",
      cell: ({ row }) => (
        <span className="text-xs font-medium">{row.original.descricao}</span>
      ),
    },
    // Loop programático pros 9 campos info (substitui 9 cópias de ~60 linhas)
    ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map(
      (i): ColumnDef<OFRefCategoriaItemResponse> => ({
        accessorKey: `info${i}`,
        header:
          (contaConfig as any)?.[`info_compl_${i}`] ||
          `Info. Complementar ${i}`,
        cell: ({ row }) => (
          <InfoComplCell
            row={row}
            field={`info${i}`}
            label={
              (contaConfig as any)?.[`info_compl_${i}`] ||
              `Info. Complementar ${i}`
            }
            setCategoriasExternas={setCategoriasExternas}
          />
        ),
      }),
    ),
  ];

  const columsPedidos: ColumnDef<PedidoItemResponse>[] = [
    {
      id: "index",
      header: "#",
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">{row.index + 1}</span>
      ),
    },
    {
      accessorKey: "identificador",
      header: "Identificador",
      cell: ({ row }) => (
        <span className="text-sm font-medium">
          {row.original.identificador}
        </span>
      ),
    },
  ];

  const columnsEtiquetas: ColumnDef<EpcItemGerado>[] = [
    {
      accessorKey: "epc",
      header: "EPC",
      cell: ({ row }) => <span className="text-xs">{row.original.epc}</span>,
    },
    {
      id: "info_adicional_item",
      header: "Info individual",
      cell: ({ row }) => (
        <EpcInfoIndividualCell
          row={row}
          travarQtdErp={impressaoConfig?.travar_quantidade_erp}
          setEpcs={setEpcs}
        />
      ),
    },
    {
      id: "preview",
      header: "Preview",
      cell: ({ row }) => (
        <PreviewCell item={row.original} coletaZplPadrao={coletaZplPadrao} />
      ),
    },
  ];

  const podeImprimir =
    categoriasExternas.length > 0 &&
    (todosResolvidosPorInfo || !!localizacaoSelecionada);

  const eansDuplicados = [
    ...new Set(
      categoriasExternas
        .map((c) => c.ean)
        .filter((ean, _idx, arr) => arr.indexOf(ean) !== arr.lastIndexOf(ean)),
    ),
  ];

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Importar Pedidos
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Busque ordens de fabricação no ERP e gere etiquetas a partir dos
            itens do pedido
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-md border bg-card px-3 h-9">
            <Switch
              id="of-repetidos"
              disabled={isLoading}
              checked={permitirItensRepetidos}
              onCheckedChange={(checked) => {
                requireAdmin(() => {
                  setPermitirItensRepetidos(checked);
                  localStorage.setItem(
                    "permitirItensRepetidos",
                    String(checked),
                  );
                });
              }}
            />
            <Label htmlFor="of-repetidos" className="text-xs cursor-pointer">
              Permitir repetidos
            </Label>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>

          <Button
            size="sm"
            onClick={gerarListaParaImpressao}
            disabled={!podeImprimir || isLoading}
            title={
              !categoriasExternas.length
                ? "Busque uma OF para imprimir"
                : !podeImprimir
                  ? "Selecione uma localização"
                  : `Imprimir ${epcs.length} etiqueta${epcs.length === 1 ? "" : "s"}`
            }
          >
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">Imprimir</span>
            {epcs.length > 0 && (
              <Badge
                variant="secondary"
                className="ml-1 h-5 px-1.5 text-[10px] bg-white/20 text-primary-foreground hover:bg-white/20"
              >
                {epcs.length}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* 1. Buscar OF */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
              1
            </div>
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <PackagePlus className="h-4 w-4 text-muted-foreground" />
              Buscar ordem de fabricação
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (categoriasExternas.length === 0)
                pesquisarErpExterno(codigoOfRef);
            }}
            className="flex flex-col sm:flex-row gap-2"
          >
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                disabled={isLoading || categoriasExternas.length > 0}
                id="impressao-of-codigo"
                type="text"
                value={codigoOfRef}
                onChange={(e) => setCodigoOfRef(e.target.value)}
                placeholder="Código da OF..."
                className="pl-8"
                required
              />
            </div>
            <div className="flex gap-2">
              {categoriasExternas.length > 0 ? (
                <Button
                  key="of-limpar"
                  type="button"
                  variant="outline"
                  onClick={limparResultados}
                  disabled={isLoading}
                  className="flex-1 sm:flex-initial"
                >
                  <X className="h-4 w-4" />
                  Limpar resultados
                </Button>
              ) : (
                <Button
                  key="of-buscar"
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 sm:flex-initial"
                >
                  <Search className="h-4 w-4" />
                  Buscar OF
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Aviso de conversão de códigos não-hex */}
      {codigosConvertidos.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-3 flex items-start gap-2 text-xs">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1 text-amber-700 dark:text-amber-300 min-w-0">
              <span className="font-medium">
                {codigosConvertidos.length} código(s) com caracteres fora do
                padrão hexadecimal foram convertidos automaticamente.
              </span>
              <span className="opacity-80">
                O código original veio do ERP, mas o EPC será gerado com o
                código convertido.
              </span>
              <ul className="mt-1 list-disc list-inside space-y-0.5 max-h-24 overflow-y-auto">
                {codigosConvertidos.map(({ original, convertido }) => (
                  <li key={original}>
                    <span className="">{original}</span>
                    <span className="mx-1">→</span>
                    <span className="font-medium">{convertido}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pedidos iNOVAONE (visível quando ainda não há resultado) */}
      {!categoriasExternas.length && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Inbox className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Pedidos iNOVAONE
                </CardTitle>
              </div>
              <span className="text-[11px] text-muted-foreground">
                Clique numa linha pra carregar
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {pedidos.length === 0 ? (
              <div className="py-10 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <Inbox className="h-8 w-8 opacity-40" />
                <p className="text-sm">Nenhum pedido disponível.</p>
              </div>
            ) : (
              <DataTable
                onRowClick={(row) => {
                  setCodigoOfRef(row.identificador);
                  pesquisarErpInovaone(row.identificador);
                }}
                columns={columsPedidos}
                data={pedidos}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Após buscar: localizações + categorias + etiquetas */}
      {categoriasExternas.length > 0 && (
        <div
          className={cn(
            "flex flex-col gap-4 md:gap-6",
            "motion-reduce:animate-none",
            resultadosSaindo
              ? "animate-out fade-out-0 slide-out-to-top-2 duration-200 ease-in fill-mode-forwards"
              : "animate-in fade-in-0 slide-in-from-top-2 duration-300 ease-out",
          )}
        >
          {/* 2. Localizações (oculta quando todos os itens têm localização
              definida pela Info. Complementar) */}
          {!todosResolvidosPorInfo && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    2
                  </div>
                  <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    Localizações
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {locPorInfoAtiva && itensSemLocalizacao > 0 && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      {itensSemLocalizacao}{" "}
                      {itensSemLocalizacao === 1
                        ? "item não teve"
                        : "itens não tiveram"}{" "}
                      localização definida pela Info. Complementar. A seleção
                      abaixo será usada para definir a localização{" "}
                      {itensSemLocalizacao === 1
                        ? "desse item"
                        : "desses itens"}
                      .
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="grid gap-2">
                    <Label
                      htmlFor="of-loc-1"
                      className="text-xs text-muted-foreground"
                    >
                      Nível 1 <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      disabled={isLoading}
                      value={localizacaoSelecionada}
                      onValueChange={(value) => {
                        setLocalizacaoSelecionada(value);
                        setLocalizacaoSelecionada2("");
                        setLocalizacaoSelecionada3("");
                        setLocalizacaoSelecionada4("");
                      }}
                    >
                      <SelectTrigger id="of-loc-1" className="w-full">
                        <SelectValue placeholder="Não definido" />
                      </SelectTrigger>
                      <SelectContent>
                        {localizacoes.map((l) => (
                          <SelectItem key={l._id} value={l._id}>
                            {l.descricao}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label
                      htmlFor="of-loc-2"
                      className="text-xs text-muted-foreground"
                    >
                      Nível 2
                    </Label>
                    <Select
                      disabled={isLoading || !localizacaoSelecionada}
                      value={localizacaoSelecionada2}
                      onValueChange={(v) => {
                        setLocalizacaoSelecionada2(v);
                        setLocalizacaoSelecionada3("");
                        setLocalizacaoSelecionada4("");
                      }}
                    >
                      <SelectTrigger id="of-loc-2" className="w-full">
                        <SelectValue placeholder="Não definido" />
                      </SelectTrigger>
                      <SelectContent>
                        {localizacoes
                          .find((n1) => n1._id === localizacaoSelecionada)
                          ?.children.map((n2) => (
                            <SelectItem key={n2._id} value={n2._id}>
                              {n2.descricao}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label
                      htmlFor="of-loc-3"
                      className="text-xs text-muted-foreground"
                    >
                      Nível 3
                    </Label>
                    <Select
                      disabled={
                        isLoading ||
                        !localizacaoSelecionada ||
                        !localizacaoSelecionada2
                      }
                      value={localizacaoSelecionada3}
                      onValueChange={(v) => {
                        setLocalizacaoSelecionada3(v);
                        setLocalizacaoSelecionada4("");
                      }}
                    >
                      <SelectTrigger id="of-loc-3" className="w-full">
                        <SelectValue placeholder="Não definido" />
                      </SelectTrigger>
                      <SelectContent>
                        {localizacoes
                          .find((n1) => n1._id === localizacaoSelecionada)
                          ?.children.find(
                            (n2) => n2._id === localizacaoSelecionada2,
                          )
                          ?.children.map((n3) => (
                            <SelectItem key={n3._id} value={n3._id}>
                              {n3.descricao}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label
                      htmlFor="of-loc-4"
                      className="text-xs text-muted-foreground"
                    >
                      Nível 4
                    </Label>
                    <Select
                      disabled={
                        isLoading ||
                        !localizacaoSelecionada ||
                        !localizacaoSelecionada2 ||
                        !localizacaoSelecionada3
                      }
                      value={localizacaoSelecionada4}
                      onValueChange={setLocalizacaoSelecionada4}
                    >
                      <SelectTrigger id="of-loc-4" className="w-full">
                        <SelectValue placeholder="Não definido" />
                      </SelectTrigger>
                      <SelectContent>
                        {localizacoes
                          .find((n1) => n1._id === localizacaoSelecionada)
                          ?.children.find(
                            (n2) => n2._id === localizacaoSelecionada2,
                          )
                          ?.children.find(
                            (n3) => n3._id === localizacaoSelecionada3,
                          )
                          ?.children.map((n4) => (
                            <SelectItem key={n4._id} value={n4._id}>
                              {n4.descricao}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* 3. Categorias + Etiquetas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                      3
                    </div>
                    <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                      <PackagePlus className="h-4 w-4 text-muted-foreground" />
                      Categorias do pedido
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    {eansDuplicados.length > 0 && (
                      <TooltipProvider delayDuration={250}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className="text-[10px] text-blue-700 dark:text-blue-400 border-blue-500/30 bg-blue-500/10 cursor-default gap-1"
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {eansDuplicados.length} EAN
                              {eansDuplicados.length > 1 ? "s" : ""} com
                              múltiplas entradas
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-64">
                            Existem itens com o mesmo EAN mas com informações
                            complementares diferentes. Eles foram mantidos como
                            entradas separadas e terão seriais consecutivos na
                            geração dos EPCs.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <EditarEmMassaButton
                      travarQtdErp={impressaoConfig?.travar_quantidade_erp}
                      targetsProduto={targetsProduto}
                      campoQuantidadePedidos={
                        impressaoConfig?.campo_quantidade_pedidos
                      }
                      infoLabels={[1, 2, 3, 4, 5, 6, 7, 8, 9].map(
                        (i) =>
                          (contaConfig as any)?.[`info_compl_${i}`] ||
                          `Info. Complementar ${i}`,
                      )}
                      setCategoriasExternas={setCategoriasExternas}
                    />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Clique na quantidade ou nos campos pra editar.
                </p>
              </CardHeader>
              <CardContent>
                <DataTable
                  columns={columnsOFREFCategoria}
                  data={categoriasExternas}
                  autoResetPageIndex={false}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                      4
                    </div>
                    <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      Etiquetas geradas
                    </CardTitle>
                  </div>
                  <Badge variant="secondary" className="font-medium">
                    {epcs.length} {epcs.length === 1 ? "etiqueta" : "etiquetas"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <DataTable columns={columnsEtiquetas} data={epcs} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* AlertDialog: duplicatas */}
      <AlertDialog
        open={duplicatasDialog}
        onOpenChange={(open) => {
          if (!open) setDuplicatasDialog(false);
        }}
      >
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-500/10">
                <AlertTriangle className="h-5 w-5 text-blue-500" />
              </div>
              <AlertDialogTitle>
                Itens com EAN duplicado encontrados
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              A ordem contém itens com o mesmo EAN mas informações
              complementares diferentes. Selecione quais deseja incluir (mínimo
              1 por grupo).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3 my-2 pr-1">
            {(() => {
              const eanCountsDialog: Record<string, number> = {};
              pendingCategorias.forEach((c) => {
                eanCountsDialog[c.ean] = (eanCountsDialog[c.ean] || 0) + 1;
              });
              const dupEans = [
                ...new Set(
                  Object.keys(eanCountsDialog).filter(
                    (e) => eanCountsDialog[e] > 1,
                  ),
                ),
              ];
              return dupEans.map((ean) => {
                const group = pendingCategorias
                  .map((c, idx) => ({ item: c, idx }))
                  .filter(({ item }) => item.ean === ean);
                const selectedInGroup = group.filter(({ idx }) =>
                  selectedDuplicataIndices.has(idx),
                );
                return (
                  <div key={ean} className="rounded-md border p-3 space-y-2">
                    <div className="flex items-center gap-2 pb-1 border-b">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                        EAN
                      </span>
                      <span className="text-sm font-semibold">{ean}</span>
                      <Badge
                        variant="secondary"
                        className="font-normal ml-auto"
                      >
                        {group.length} entradas
                      </Badge>
                    </div>
                    <div className="space-y-1.5">
                      {group.map(({ item, idx }) => {
                        const isSelected = selectedDuplicataIndices.has(idx);
                        const isLastSelected =
                          selectedInGroup.length === 1 && isSelected;
                        const infoLabels = [
                          contaConfig?.info_compl_1 || "Info 1",
                          contaConfig?.info_compl_2 || "Info 2",
                          contaConfig?.info_compl_3 || "Info 3",
                          contaConfig?.info_compl_4 || "Info 4",
                          contaConfig?.info_compl_5 || "Info 5",
                          contaConfig?.info_compl_6 || "Info 6",
                          contaConfig?.info_compl_7 || "Info 7",
                          contaConfig?.info_compl_8 || "Info 8",
                          contaConfig?.info_compl_9 || "Info 9",
                        ];
                        const infoValues = [
                          item.info1,
                          item.info2,
                          item.info3,
                          item.info4,
                          item.info5,
                          item.info6,
                          item.info7,
                          item.info8,
                          item.info9,
                        ];
                        const infoEntries = infoValues
                          .map((val, i) => ({
                            label: infoLabels[i],
                            value: val,
                          }))
                          .filter(({ value }) => !!value);
                        return (
                          <label
                            key={idx}
                            className={`flex items-start gap-3 p-2 rounded border transition-colors select-none ${
                              isLastSelected
                                ? "cursor-not-allowed"
                                : "cursor-pointer"
                            } ${
                              isSelected
                                ? "bg-primary/5 border-primary/30"
                                : "bg-muted/30 border-border opacity-60"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isLastSelected}
                              className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                              onChange={(e) => {
                                setSelectedDuplicataIndices((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(idx);
                                  else next.delete(idx);
                                  return next;
                                });
                              }}
                            />
                            <div className="flex flex-col gap-0.5 text-xs min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">
                                  {item.descricao}
                                </span>
                                <Badge
                                  variant="secondary"
                                  className="font-normal"
                                >
                                  Qtd: {item.quantidade}
                                </Badge>
                              </div>
                              {infoEntries.length > 0 && (
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-muted-foreground">
                                  {infoEntries.map(({ label, value }) => (
                                    <span key={label}>
                                      <span className="font-medium">
                                        {label}:
                                      </span>{" "}
                                      {value}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const eanCountsFinal: Record<string, number> = {};
                pendingCategorias.forEach((c) => {
                  eanCountsFinal[c.ean] = (eanCountsFinal[c.ean] || 0) + 1;
                });
                const result = pendingCategorias.filter((c, idx) =>
                  eanCountsFinal[c.ean] > 1
                    ? selectedDuplicataIndices.has(idx)
                    : true,
                );
                setCodigosConvertidos(pendingConversoes);
                setCategoriasExternas(result);
                setTargetsProduto(
                  result.map(
                    (c) =>
                      (c.quantidade || 1) * getQuantidadeBoFromCategoria(c),
                  ),
                );
                setDuplicatasDialog(false);
              }}
            >
              Confirmar seleção
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog: EPCs repetidos */}
      <AlertDialog
        open={epcRepetidosDialog}
        onOpenChange={setEpcRepetidosDialog}
      >
        <AlertDialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <AlertDialogTitle>EPCs repetidos encontrados</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              Os EPCs abaixo já existem na base. Sincronize os dados e tente
              novamente, ou ative "Permitir repetidos" para imprimir mesmo
              assim.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="overflow-y-auto max-h-64 rounded-md border">
            <ul className="divide-y">
              {epcRepetidosEncontrados.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-3 py-2 gap-2"
                >
                  <span className="text-xs truncate">{item.epc}</span>
                  <Badge variant="secondary" className="">
                    {item.codigo}
                  </Badge>
                </li>
              ))}
            </ul>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => setEpcRepetidosDialog(false)}>
              <Eye className="h-4 w-4" />
              Entendi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ImportarOFREF;
